import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import adminApi from "../../api/adminApi";
import "./Admin.css";

const normalizeQuestions = (questions = []) =>
  questions.map((q) => ({
    questionText: String(q.questionText || "").trim(),
    imageUrl: String(q.imageUrl || "").trim(),
    imagePublicId: String(q.imagePublicId || "").trim(),
    imageAlt: String(q.imageAlt || "").trim(),
    codeSnippet: String(q.codeSnippet || ""),
    codeLanguage: String(q.codeLanguage || "").trim(),
    options: Array.isArray(q.options) ? q.options.map((o) => String(o || "").trim()) : [],
    correctAnswer: String(q.correctAnswer || "").trim(),
  }));

const toEditForm = (quiz) => ({
  id: quiz._id,
  quizId: quiz.quizId || "",
  title: quiz.title || "",
  description: quiz.description || "",
  level: quiz.level || "Easy",
  timeLimit: quiz.timeLimit || 30,
  passMarks: quiz.passMarks || 1,
  technologies: Array.isArray(quiz.technologies) ? quiz.technologies.join(", ") : "",
  questionsJson: JSON.stringify(normalizeQuestions(quiz.questions || []), null, 2),
  status: quiz.status || "DRAFT",
  attemptsCount: quiz.attemptsCount || 0,
});

const AdminManageExams = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "", isActive: "" });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingQuiz, setEditingQuiz] = useState(null);
  const limit = 10;

  const quizKpis = useMemo(() => {
    const published = quizzes.filter((quiz) => quiz.status === "PUBLISHED").length;
    const draft = quizzes.filter((quiz) => quiz.status === "DRAFT").length;
    const archived = quizzes.filter((quiz) => quiz.status === "ARCHIVED").length;
    const active = quizzes.filter((quiz) => quiz.isActive).length;
    return { published, draft, archived, active, total: quizzes.length };
  }, [quizzes]);

  const loadQuizzes = async (pageNo = page, filterState = filters) => {
    try {
      setLoading(true);
      setError("");
      const params = {
        page: pageNo,
        limit,
        ...Object.fromEntries(Object.entries(filterState).filter(([, v]) => v !== "")),
      };
      const res = await adminApi.getQuizzes(params);
      setQuizzes(res.data?.quizzes || []);
      setPagination(res.data?.pagination || { totalPages: 1, total: 0 });
    } catch (err) {
      setError(err?.message || "Failed to load exams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuizzes(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    loadQuizzes(1, filters);
  };

  const handlePage = (nextPage) => {
    const normalized = Math.max(1, Math.min(nextPage, pagination.totalPages || 1));
    setPage(normalized);
    loadQuizzes(normalized, filters);
  };

  const refreshAfterAction = async (message) => {
    setSuccess(message);
    await loadQuizzes(page, filters);
  };

  const toggleActive = async (quiz) => {
    if (quiz.status !== "PUBLISHED") {
      setError("Only published exams can be activated or deactivated.");
      return;
    }

    try {
      setError("");
      setSuccess("");
      await adminApi.updateQuiz(quiz._id, { isActive: !quiz.isActive });
      await refreshAfterAction(quiz.isActive ? "Exam deactivated." : "Exam activated.");
    } catch (err) {
      setError(err?.message || "Failed to update exam activity");
    }
  };

  const togglePublish = async (quiz) => {
    try {
      setError("");
      setSuccess("");
      const nextStatus = quiz.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED";
      await adminApi.updateQuiz(quiz._id, { status: nextStatus });
      await refreshAfterAction(nextStatus === "PUBLISHED" ? "Exam published." : "Exam unpublished.");
    } catch (err) {
      setError(err?.message || "Failed to update publication status");
    }
  };

  const archiveQuiz = async (quiz) => {
    if (quiz.status === "ARCHIVED") return;

    const confirmed = window.confirm(`Archive "${quiz.title}"? Students will no longer see it.`);
    if (!confirmed) return;

    try {
      setError("");
      setSuccess("");
      await adminApi.archiveQuiz(quiz._id);
      await refreshAfterAction("Exam archived.");
    } catch (err) {
      setError(err?.message || "Failed to archive exam");
    }
  };

  const canDeleteQuiz = (quiz) =>
    ["DRAFT", "ARCHIVED"].includes(quiz.status) && (quiz.attemptsCount || 0) === 0;

  const deleteQuiz = async (quiz) => {
    if (!canDeleteQuiz(quiz)) {
      setError("Only draft exams with no attempts can be deleted.");
      return;
    }

    const confirmed = window.confirm(`Delete "${quiz.title}" permanently?`);
    if (!confirmed) return;

    try {
      setError("");
      setSuccess("");
      await adminApi.deleteQuiz(quiz._id);
      await refreshAfterAction("Exam deleted.");
    } catch (err) {
      setError(err?.message || "Failed to delete exam");
    }
  };

  const openEdit = (quiz) => {
    setError("");
    setSuccess("");
    setEditingQuiz(toEditForm(quiz));
  };

  const updateEditField = (field, value) => {
    setEditingQuiz((prev) => ({ ...prev, [field]: value }));
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingQuiz) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const canEditQuestions = editingQuiz.attemptsCount === 0;
      const payload = {
        title: editingQuiz.title.trim(),
        description: editingQuiz.description.trim(),
        level: editingQuiz.level,
        timeLimit: Number(editingQuiz.timeLimit),
        passMarks: Number(editingQuiz.passMarks),
        technologies: editingQuiz.technologies
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      };

      if (!payload.title) throw new Error("Title is required.");
      if (canEditQuestions) {
        const parsedQuestions = JSON.parse(editingQuiz.questionsJson);
        if (!Array.isArray(parsedQuestions) || parsedQuestions.length === 0) {
          throw new Error("Questions JSON must be a non-empty array.");
        }
        payload.questions = normalizeQuestions(parsedQuestions);
        if (payload.passMarks > payload.questions.length) {
          throw new Error("Pass marks cannot be greater than total questions.");
        }
      }

      await adminApi.updateQuiz(editingQuiz.id, payload);
      setEditingQuiz(null);
      refreshAfterAction("Exam updated.");
    } catch (err) {
      setError(err?.message || "Failed to update exam");
    } finally {
      setSaving(false);
    }
  };

  const getStatusClass = (status) => {
    if (status === "PUBLISHED") return "published";
    if (status === "ARCHIVED") return "archived";
    return "draft";
  };

  const getAvailabilityText = (quiz) => {
    if (quiz.status !== "PUBLISHED") return "Unavailable";
    return quiz.isActive ? "Active" : "Inactive";
  };

  return (
    <section className="admin-page">
      <div className="admin-split-head">
        <div>
          <h2>Manage Exams</h2>
          <p>Control exam visibility, availability, and content from one focused workspace.</p>
        </div>
        <div className="actions-row">
          <Link className="admin-link-button" to="/admin/quizzes">Create Exam</Link>
          <Link className="admin-link-button secondary" to="/admin/exam-guide">Exam Guide</Link>
        </div>
      </div>

      <form className="admin-card admin-filters" onSubmit={applyFilters}>
        <input
          type="text"
          placeholder="Search by title or exam ID"
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
        />
        <select
          value={filters.status}
          onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
        >
          <option value="">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="PUBLISHED">Published</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        <select
          value={filters.isActive}
          onChange={(e) => setFilters((prev) => ({ ...prev, isActive: e.target.value }))}
        >
          <option value="">All Activity</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <button type="submit">Apply</button>
      </form>

      <div className="admin-card admin-quiz-kpi-row compact">
        <div className="admin-quiz-kpi">
          <span>Total</span>
          <strong>{quizKpis.total}</strong>
        </div>
        <div className="admin-quiz-kpi published">
          <span>Published</span>
          <strong>{quizKpis.published}</strong>
        </div>
        <div className="admin-quiz-kpi draft">
          <span>Draft</span>
          <strong>{quizKpis.draft}</strong>
        </div>
        <div className="admin-quiz-kpi archived">
          <span>Archived</span>
          <strong>{quizKpis.archived}</strong>
        </div>
        <div className="admin-quiz-kpi active">
          <span>Active</span>
          <strong>{quizKpis.active}</strong>
        </div>
      </div>

      {editingQuiz ? (
        <form className="admin-card admin-form manage-edit-panel" onSubmit={submitEdit}>
          <div className="admin-question-head">
            <div>
              <h3>Edit Exam</h3>
              <p>{editingQuiz.quizId}</p>
            </div>
            <button type="button" className="secondary" onClick={() => setEditingQuiz(null)}>Close</button>
          </div>

          <div className="admin-form-grid">
            <input
              type="text"
              placeholder="Title"
              value={editingQuiz.title}
              onChange={(e) => updateEditField("title", e.target.value)}
              required
            />
            <select value={editingQuiz.level} onChange={(e) => updateEditField("level", e.target.value)}>
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
            <input
              type="number"
              min="1"
              placeholder="Time limit"
              value={editingQuiz.timeLimit}
              onChange={(e) => updateEditField("timeLimit", e.target.value)}
            />
            <input
              type="number"
              min="1"
              placeholder="Pass marks"
              value={editingQuiz.passMarks}
              onChange={(e) => updateEditField("passMarks", e.target.value)}
            />
            <textarea
              placeholder="Description"
              rows={3}
              value={editingQuiz.description}
              onChange={(e) => updateEditField("description", e.target.value)}
            />
            <input
              type="text"
              placeholder="Technologies (comma separated)"
              value={editingQuiz.technologies}
              onChange={(e) => updateEditField("technologies", e.target.value)}
            />
          </div>

          {editingQuiz.attemptsCount > 0 ? (
            <div className="admin-state">
              This exam already has attempts. You can edit details, but questions are locked.
            </div>
          ) : (
            <div className="admin-json-block">
              <label htmlFor="edit-questions-json">Questions JSON</label>
              <textarea
                id="edit-questions-json"
                rows={8}
                className="admin-json-input"
                value={editingQuiz.questionsJson}
                onChange={(e) => updateEditField("questionsJson", e.target.value)}
              />
            </div>
          )}

          <div className="actions-row">
            <button type="submit" disabled={saving}>{saving ? "Saving..." : "Save Edit"}</button>
            <button type="button" className="secondary" onClick={() => setEditingQuiz(null)}>Cancel</button>
          </div>
        </form>
      ) : null}

      {success ? <div className="admin-state">{success}</div> : null}
      {error ? <div className="admin-state admin-error">{error}</div> : null}
      {loading ? <div className="admin-state">Loading exams...</div> : null}

      {!loading ? (
        <div className="admin-card admin-table-wrap">
          <table className="admin-table manage-exam-table">
            <thead>
              <tr>
                <th>Exam</th>
                <th>Level</th>
                <th>Status</th>
                <th>Availability</th>
                <th>Attempts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {quizzes.map((quiz) => (
                <tr key={quiz._id}>
                  <td>
                    <strong>{quiz.title}</strong>
                    <div className="status-subtext">{quiz.quizId}</div>
                  </td>
                  <td>{quiz.level}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(quiz.status)}`}>{quiz.status}</span>
                  </td>
                  <td>
                    <span className={`pill ${quiz.isActive ? "ok" : "warn"}`}>
                      {getAvailabilityText(quiz)}
                    </span>
                  </td>
                  <td>{quiz.attemptsCount || 0}</td>
                  <td>
                    <div className="manage-actions">
                      <button
                        type="button"
                        className={`manage-action ${quiz.isActive ? "danger-soft" : "success-soft"}`}
                        onClick={() => toggleActive(quiz)}
                        disabled={quiz.status !== "PUBLISHED"}
                      >
                        {quiz.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button type="button" className="manage-action neutral" onClick={() => openEdit(quiz)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className={`manage-action ${quiz.status === "PUBLISHED" ? "warning-soft" : "primary-soft"}`}
                        onClick={() => togglePublish(quiz)}
                        disabled={quiz.status === "ARCHIVED"}
                      >
                        {quiz.status === "PUBLISHED" ? "Unpublish" : "Publish"}
                      </button>
                      <button
                        type="button"
                        className="manage-action archive-soft"
                        onClick={() => archiveQuiz(quiz)}
                        disabled={quiz.status === "ARCHIVED"}
                      >
                        Archive
                      </button>
                      <button
                        type="button"
                        className="manage-action delete-soft"
                        onClick={() => deleteQuiz(quiz)}
                        disabled={!canDeleteQuiz(quiz)}
                        title={
                          canDeleteQuiz(quiz)
                            ? "Delete exam permanently"
                            : "Only draft or archived exams with no attempts can be deleted"
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="admin-pagination">
            <button type="button" onClick={() => handlePage(page - 1)} disabled={page <= 1}>Prev</button>
            <span>
              Page {page} of {pagination.totalPages || 1} ({pagination.total || 0} exams)
            </span>
            <button
              type="button"
              onClick={() => handlePage(page + 1)}
              disabled={page >= (pagination.totalPages || 1)}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AdminManageExams;
